import React, { useState, useCallback } from "react";
import { usePostSelQuestions } from "../../useStoryOperations";
import apiPaths from "../../config/apiPaths";
import { continueGem, homeBtn, yetiPreview } from "../../../../appAssets/index";
import WebLottiePlayer from "../WebLottiePlayer";
import WebShadowButton from "../WebShadowButton";
import WebAudioPlayer from "../WebAudioPlayer";
import { WebLoader } from "../../../../components/Loader";

interface SelOption {
  option: string;
  optionValue: string;
  optionView: string;
  optionAudioUrl: string;
  optionAudioDuration: string;
  optionViewAudioUrl: string;
  optionViewAudioDuration: string;
}

export interface SelQuestionsData {
  documentId: string;
  question: string;
  audioUrl: string;
  audioDuration: string;
  storyName: string;
  storyImageUrl: string;
  selCompetency: string;
  options: SelOption[];
}

interface WebSelQuestionsModalProps {
  questionsData: SelQuestionsData | null;
  onContinue: () => void;
  continueToStory: () => void;
  storyId?: string;
  userData?: {
    userName?: string;
    schoolHash?: string;
    grade?: string;
    grades?: { grade: string; complexity: string }[];
  };
  contentBaseUrl?: string;
  onNavigateHome: () => void;
}

const formatCompetency = (c: string): string =>
  !c ? "" : c.trim().toUpperCase().replace(/\s+/g, "_");

const normalizeEllipsis = (str: string): string =>
  str?.replace(/…\s*$/, ".") ?? "";

const WebSelQuestionsModal: React.FC<WebSelQuestionsModalProps> = ({
  questionsData,
  continueToStory,
  storyId,
  userData,
  contentBaseUrl,
  onNavigateHome,
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [currentAudioUrl, setCurrentAudioUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState<SelOption | null>(null);
  const [optionStep, setOptionStep] = useState(0);
  const { postSelQuestions } = usePostSelQuestions();

  const handleOptionSelect = useCallback((option: SelOption) => {
    setSelectedItem(option);
    setSelectedOption(option.optionValue);
    setCurrentAudioUrl(option.optionAudioUrl);
  }, []);

  const handleContinue = async () => {
    if (!selectedItem || !questionsData) return;
    if (!userData?.userName || !userData?.schoolHash || !userData?.grade) {
      setOptionStep(1);
      setSelectedOption(null);
      setCurrentAudioUrl(selectedItem.optionViewAudioUrl);
      return;
    }
    setIsLoading(true);
    const params = {
      userId: userData?.userName,
      schoolHash: userData?.schoolHash,
      classId: userData?.grade ?? "",
      source_type: "Child",
      pulses: [
        {
          competency: formatCompetency(questionsData.selCompetency),
          S: parseInt(selectedItem.optionValue, 10),
          W: 0.3,
          I: 1.0,
          E: 1.0,
          storyId: storyId ?? "",
        },
      ],
    };
    const data = await postSelQuestions(params);
    setIsLoading(false);
    if (data?.success) {
      setOptionStep(1);
      setSelectedOption(null);
      setCurrentAudioUrl(selectedItem.optionViewAudioUrl);
    }
  };

  const optionJsonValue =
    selectedItem?.optionValue === "3"
      ? apiPaths.STORY_OPTION_VIEW2
      : selectedItem?.optionValue === "4"
        ? apiPaths.STORY_OPTION_VIEW3
        : apiPaths.STORY_OPTION_VIEW1;

  if (!questionsData) return null;

  return (
    <div className="web-sel-overlay">
      {/* Characters pinned to bottom of full-screen overlay */}
      {optionStep === 0 && (
        <div className="web-sel-chars">
          <img src={yetiPreview} alt="" className="web-sel-char-img" />
          <WebLottiePlayer
            source={questionsData.storyImageUrl}
            className="web-sel-char-lottie"
            autoPlay
            loop
          />
        </div>
      )}

      {optionStep === 0 && (
        <div className="web-sel-container">
          <p className="web-sel-question">{questionsData.question}</p>

          <div className="web-sel-options-scroll">
            <div className="web-sel-options-grid">
              {questionsData.options.map((option) => (
                <button
                  key={option.optionValue}
                  type="button"
                  className={`web-sel-option${selectedOption === option.optionValue ? " web-sel-option--selected" : ""}`}
                  onClick={() => handleOptionSelect(option)}
                >
                  {option.option}
                </button>
              ))}
            </div>
          </div>

          <div className="web-sel-continue-row">
            <WebShadowButton
              title="Continue"
              disabled={selectedOption === null}
              onPress={handleContinue}
            />
          </div>

          {currentAudioUrl ? (
            <WebAudioPlayer
              key={currentAudioUrl}
              uri={currentAudioUrl}
              contentBaseUrl={contentBaseUrl}
            />
          ) : questionsData.audioUrl ? (
            <WebAudioPlayer
              key="question-audio"
              uri={questionsData.audioUrl}
              contentBaseUrl={contentBaseUrl}
            />
          ) : null}
        </div>
      )}

      {optionStep === 1 && (
        <div className="web-sel-container">
          <div className="web-sel-response-container">
            <p className="web-sel-response-text">
              {normalizeEllipsis(selectedItem?.optionView ?? "")}
            </p>
            <WebLottiePlayer
              source={optionJsonValue}
              className="web-sel-response-lottie"
              autoPlay
              loop
            />
          </div>

          {currentAudioUrl ? (
            <WebAudioPlayer
              uri={currentAudioUrl}
              contentBaseUrl={contentBaseUrl}
            />
          ) : null}

          <div className="web-sel-action-row">
            <button
              type="button"
              className="web-sel-action-btn web-sel-home-btn"
              onClick={onNavigateHome}
              aria-label="Go home"
            >
              <img src={homeBtn} alt="Home" className="web-sel-btn-img" />
            </button>
            <button
              type="button"
              className="web-sel-action-btn web-sel-continue-btn"
              onClick={continueToStory}
              aria-label="Continue story"
            >
              <img
                src={continueGem}
                alt="Continue"
                className="web-sel-btn-img"
              />
            </button>
          </div>
        </div>
      )}

      {isLoading && <WebLoader />}
    </div>
  );
};

export default WebSelQuestionsModal;
